import { customElement, observable, GenesisElement, attr } from '@genesislcap/web-core';
import { TrancheDetailFlyoutStyles as styles } from './tranche-detail-flyout.styles';
import { TrancheDetailFlyoutTemplate as template } from './tranche-detail-flyout.template';
import { EventEmitter } from '@genesislcap/foundation-events';
import { submitBondBid } from '../../../../mbm/utils/bond-bid-utils';


@customElement({
  name: 'tranche-detail-flyout',
  template,
  styles,
})
export class TrancheDetailFlyout extends EventEmitter(GenesisElement) {
  
  @attr public rowData: any = {};
  @attr public flyoutElement: HTMLElement;
  @observable cusip = '';
  @observable issuer = '';
  @observable issuerRatings = '';
  @observable coupon = '';
  @observable maturityDate = '';
  @observable callDate = '';
  @observable receivedTime = '';
  @observable dueTime = '';
  @observable firmTime = '';
  @observable status = '';
  @observable state = '';
  @observable sector = '';
  @observable outstandingVolume = '';
  @observable preRefundDate = '';
  @observable workoutDate = '';
  @observable callPrice = '';
  @observable settlementDate = '';
  @observable taxStatus = '';
  @observable quoteRequestSize = '';
  @observable price = '';
  @observable priceType = '';
  @observable bidPriceType = '';
  @observable bondPriceType = '';
  @observable activeTab = 'overview';
  @observable isResizing = false;
  @observable flyoutWidth = 575;
  @observable amt = '';
  @observable insurer = '';
  @observable calculatedBidPrice = '';
  @observable source = '';
  @observable calculatedYield = '';
  @observable isDatasourceConnected = false;

  private resizeStartX = 0;
  private initialWidth = 0;

  disconnectedCallback() {
    super.disconnectedCallback();
    // Clean up any lingering event listeners
    if (this.isResizing) {
      document.removeEventListener('mousemove', this.handleMouseMove);
      document.removeEventListener('mouseup', this.handleMouseUp);
    }
  }

  private handleMouseMove = (event: MouseEvent) => {
    if (!this.isResizing) return;
    
    const deltaX = event.clientX - this.resizeStartX;
    const newWidth = this.initialWidth - deltaX;
    
    this.flyoutWidth = Math.max(400, Math.min(1500, newWidth));
    
    // Use requestAnimationFrame for smooth updates
    requestAnimationFrame(() => {
      this.updateFlyoutWidth();
    });
  };

/** Response Type dropdown: lock when bond forces type or RFQ not DUE_IN (not locked after submit if bond type was None) */
isResponseTypeDisabled(): boolean {
  const hasBondPriceType = !!(this.bondPriceType && this.bondPriceType !== 'None');
  const isNotDueIn = this.status !== 'DUE_IN';

  return hasBondPriceType || isNotDueIn;
}

/** Value used for submit + validation when column is hidden */
getEffectivePriceType(): string {
  if (this.priceType && this.priceType !== 'None') return this.priceType;
  if (this.bondPriceType && this.bondPriceType !== 'None') return this.bondPriceType;
  if (this.bidPriceType && this.bidPriceType !== 'None') return this.bidPriceType;
  const rowType = this.rowData?.BID_PRICE_TYPE;
  if (rowType && rowType !== 'None') return String(rowType);
  return '';
}

/** Submit: need price + DUE_IN + a real response type (including locked submitted bid) */
isSubmitDisabled(): boolean {
  if (!this.price || this.status !== 'DUE_IN') return true;
  return !this.getEffectivePriceType();
}

  private handleMouseUp = () => {
    this.isResizing = false;
    this.classList.remove('resizing');
    
    // Remove resizing class from the flyout
    if (this.flyoutElement) {
      this.flyoutElement.classList.remove('resizing');
    }
    
    // Remove event listeners when resizing ends
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);
  };

  private updateFlyoutWidth() {
    if (this.flyoutElement) {
      this.flyoutElement.style.setProperty('--flyout-width', `${this.flyoutWidth}px`);
      this.flyoutElement.style.width = `${this.flyoutWidth}px`;
    }
  }

  public startResize(event: MouseEvent) {
    event.preventDefault();
    this.isResizing = true;
    this.resizeStartX = event.clientX;
    this.initialWidth = this.flyoutWidth;
    this.classList.add('resizing');
    
    // Add resizing class to the flyout for smooth resize
    if (this.flyoutElement) {
      this.flyoutElement.classList.add('resizing');
    }
    
    // Add event listeners only when resizing starts
    document.addEventListener('mousemove', this.handleMouseMove);
    document.addEventListener('mouseup', this.handleMouseUp);
  }

  public setRowData(row: any) {
    this.rowData = row;

    this.cusip = row.CUSIP || '';
    this.issuer = row.ISSUER_NAME || '';
    this.issuerRatings = row.ISSUER_RATINGS || '';
    this.coupon = row.COUPON_RATE || '';
    this.maturityDate = row.MATURITY_DATE || '';
    this.callDate = row.CALL_DATE || '';
    this.receivedTime = row.RECEIVED_TIME || '';
    this.dueTime = row.DUE_TIME || '';
    this.firmTime = row.FIRM_TIME || '';
    this.status = row.STATUS || '';
    this.state = row.STATE || '';
    this.sector = row.SECTOR || '';
    this.outstandingVolume = row.OUTSTANDING_VOLUME || '';
    this.preRefundDate = row.PRE_REFUND_DATE || '';
    this.workoutDate = row.WORKOUT_DATE || '';
    this.callPrice = row.CALL_PRICE || '';
    this.settlementDate = row.SETTLEMENT_DATE || '';
    this.taxStatus = row.TAX_STATUS || '';
    this.quoteRequestSize = row.IOI_SIZE || '';
    this.amt = row.AMT || '';
    this.insurer = row.INSURER || '';
    this.source = row.SOURCE || '';
    this.calculatedBidPrice = row.CALCULATED_BID_PRICE || '';
    this.calculatedYield = row.CALCULATED_YIELD || '';

    // Use grid row values if present (even if empty string or zero)
    const hasBidPrice = row.BID_PRICE !== undefined && row.BID_PRICE !== null;
    const hasPriceType = row.BID_PRICE_TYPE !== undefined && row.BID_PRICE_TYPE !== null;
    const hasBondPriceType = row.BOND_PRICE_TYPE !== undefined && row.BOND_PRICE_TYPE !== null;

    this.price = hasBidPrice ? row.BID_PRICE : '';
    this.bidPriceType = hasPriceType ? row.BID_PRICE_TYPE : '';
    this.bondPriceType = hasBondPriceType ? row.BOND_PRICE_TYPE : '';

    this.priceType = this.bondPriceType && this.bondPriceType !== 'None' ? this.bondPriceType : this.bidPriceType && this.bidPriceType !== 'None' ? this.bidPriceType : '';
  }

  public setActiveTab(tabId: string) {
    this.activeTab = tabId;
    this.activeTabChanged(tabId);
  }

  activeTabChanged(tabId: string) {
    switch(tabId) {
      case 'overview':
        this.flyoutWidth = 500;
        this.disconnectDatasource();
        break;
      case 'history':
        this.flyoutWidth = 800;
        this.connectDatasource();
        break;
    }
    this.updateFlyoutWidth();
  }

  async handleSubmit() {
    const priceType = this.getEffectivePriceType();
    const success = await submitBondBid(
      this.rowData.BOND_ID,
      this.price,
      priceType
    );
    if (success) {
      this.$emit('close', undefined);
    }
  }

  // Called by parent when the containing flyout is closed
  public onFlyoutClosed() {
    this.disconnectDatasource();
  }

  handlePriceBlur() {
    if (this.price) {
      const numValue = parseFloat(this.price);
      if (!isNaN(numValue)) {
        switch (this.priceType) {
          case 'Spread':
            // Spread: integer only
            this.price = Math.round(numValue).toString();
            break;
          case 'Price':
            // Price: always show 3 decimal places
            this.price = numValue.toFixed(3);
            break;
          case 'Yield':
            // Yield: round to 3 decimal places for display
            this.price = numValue.toFixed(3);
            break;
        }
      }
    }
  }

  private connectDatasource() {
    if (!this.isDatasourceConnected) {
      this.isDatasourceConnected = true;
    }
  }

  private disconnectDatasource() {
    if (this.isDatasourceConnected) {
      this.isDatasourceConnected = false;
    }
  }
} 

